<template>
  <details
    v-if="attachments.length"
    class="flex-1 min-w-0 flex flex-col shrink min-h-0 list-none"
    open
  >
    <summary
      class="relative list-none text-sm leading-tight w-full flex justify-between items-center gap-2"
    >
      <p :id="labelId" class="text-gray-700 font-bold flex-1">
        <span class="font-medium text-gray-700">
          {{ t('attachments.uploadCount', 2) }}
        </span>
      </p>
      <span aria-hidden="true">
        <span
          class="hide-button mb-1 select-none text-blue-500 cursor-pointer flex items-center gap-1 opacity-50 hover:opacity-80 active:opacity-100 transition-opacity ease-out duration-150"
        >
          <s-icon name="close" class="opacity-70" size="16" />
          {{ t('attachments.hide') }}
        </span>
        <span
          class="show-button absolute select-none right-0 top-0 text-blue-500 cursor-pointer flex items-center gap-1 opacity-50 hover:opacity-80 active:opacity-100 transition-opacity ease-out duration-150"
        >
          <s-icon name="eye" class="opacity-70" size="16" />
          {{ t('attachments.show') }}
        </span>
      </span>
    </summary>
    <div class="shrink min-h-0 max-h-[14rem] pt-2.5 pb-1.5 flex flex-col">
      <ul
        :aria-labelledby="labelId"
        class="scroll-shadow shrink min-h-0 w-full flex flex-col divide-y divide-gray-200 rounded-md overflow-hidden border border-gray-200 bg-gray-50 shadow-inset overflow-y-auto"
      >
        <s-response-attachment-list-item
          v-for="attachment in attachments"
          :key="attachment.id"
          :attachment="attachment"
          :deletable="!!showDelete"
          :only-on-delete="propsToReloadOnDelete || []"
        />
      </ul>
    </div>
  </details>
  <div v-else class="flex-1">
    <p class="text-sm leading-tight font-medium italic rounded-full text-gray-600">
      {{ t('attachments.noFiles') }}
    </p>
  </div>
  <div
    v-if="errorMessages?.length"
    class="mt-2 rounded-lg overflow-hidden shadow font-medium text-base flex items-stretch"
  >
    <div class="w-2 bg-red-500"></div>
    <div
      class="flex-1 px-3 py-2 border-2 rounded-r-lg flex items-center gap-2 bg-red-100 border-red-200 text-red-700 flex items-center gap-1"
    >
      <s-icon name="alert" size="20" class="text-red-500" />
      <ul class="flex flex-col text-base leading-tight">
        <li v-for="error in errorMessages">{{ error }}</li>
      </ul>
    </div>
  </div>
</template>
<script setup lang="ts">
import {computed, ref, useId, watch} from 'vue';
import SIcon from '../design-system/SIcon.vue';
import SResponseAttachmentListItem from './SResponseAttachmentListItem.vue';
import {useI18n} from 'vue-i18n';
import {notNullish} from '../util/type-guards';

const props = defineProps<{
  errors?: Record<string, string>;
  attachments: {
    id?: number;
    filename: string;
    mimeType?: string | null;
  }[];
  responseId?: number | null;
  showDelete?: boolean;
  propsToReloadOnDelete?: string[];
}>();

const labelId = useId();

const {t} = useI18n({
  useScope: 'local',
  inheritLocale: true,
});

const errorMessages = computed(() => {
  if (!props.errors) {
    return [];
  }

  // find any error keys that start with attachments, and then return their associated values.
  return Object.keys(props.errors || {})
    .filter((key) => key.startsWith('attachments'))
    .map((key) => props.errors?.[key])
    .filter(notNullish);
});
</script>
<style scoped>
details[open] .show-button,
details:not([open]) .hide-button {
  opacity: 0;
}

details:not([open]) .show-button,
details[open] .hide-button {
  opacity: 1;
}
</style>
<style scoped>
.loader {
  --loader-color: #76cde8;

  @apply block w-8 h-8 animate-spin rounded-full mix-blend-darken after:absolute after:block after:inset-[5px] after:rounded-full after:bg-white;
  background: conic-gradient(from 180deg at 50% 50%, transparent 0deg, var(--loader-color) 360deg);
}
</style>
<i18n>
{
  "en": {
    "attachments": {
      "uploadCount": "Upload | Uploads",
      "noFiles": "No files uploaded.",
      "hide": "Hide",
      "show": "Show"
    }
  },
  "fr": {
    "attachments": {
      "uploadCount": "Upload | Uploads",
      "noFiles": "Aucun fichier téléchargé.",
      "hide": "Masquer",
      "show": "Afficher"
    }
  }
}
</i18n>
